import type { AnyEventObject } from 'xstate';

import {
  client as apolloClient,
  isAuthorizationError,
} from 'src/shared/graphql';
import type { DesignStudioThemeEnum } from 'src/shared/graphql/generated';
import { queries } from 'src/shared/graphql/requests';
import { isDevelopment } from 'src/shared/helpers/environment';
import {
  logError as captureException,
  logWarning,
} from 'src/shared/telemetry/logger';

function logError<TContext>(_: TContext, event: AnyEventObject) {
  const { data } = event;

  if (isDevelopment()) {
    // eslint-disable-next-line no-console
    console.error('Machine error\n', event);
  } else {
    // do not log if auth error as its expected
    if (data instanceof Error && isAuthorizationError(data)) {
      logWarning(data.message);
      return;
    }

    // See DS-846
    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
    captureException(data);
  }
}

async function fetchPartnerIdForJob(jobId: number) {
  const jobResponse = await queries.getJob(apolloClient, { id: jobId });
  const job = jobResponse.data.jobs.results[0];
  const captureRequestRequestingPartnerId =
    job?.captureRequest?.displayRequestingOrg?.partnerId;

  if (job?.example) {
    // If example job, user partnerId from User's Org
    const profileResponse = await queries.profile(apolloClient, {});
    const user = profileResponse?.data?.profile?.user;
    const partnerId = user.orgs[0]?.partner?.id;

    return partnerId;
  }

  // ..otherwise favor using partner of requesting org if capture request over partner from job
  return captureRequestRequestingPartnerId || job?.partnerId;
}

async function fetchMenuItems({
  themeName,
}: {
  themeName: DesignStudioThemeEnum;
}) {
  const menuItemsResponse = await queries.getMenuItems(apolloClient, {
    themeName,
  });

  return menuItemsResponse?.data?.designStudioProductData;
}

// generic actions that can be shared between machines
const sharedOptions = {
  logError,
  fetchMenuItems,
  fetchPartnerIdForJob,
};

export { sharedOptions };
